import { useCallback } from 'react';
import { useWebSocket } from './useWebSocket';
import { useEventTracking } from './useEventTracking';
import { Notification } from 'src/types/Notifications';

interface UseNotificationWebSocketOptions {
  onNotification?: (notification: Notification) => void;
  onNotificationReadStatus?: (notification: Notification) => void;
  enabled?: boolean;
}

export const useNotificationWebSocket = (
  options: UseNotificationWebSocketOptions = {}
): ReturnType<typeof useWebSocket> => {
  const { trackError } = useEventTracking();
  const { onNotification, onNotificationReadStatus, enabled = true } = options;

  const handleNotification = useCallback(
    (notificationData: Notification): void => {
      if (onNotification) {
        onNotification(notificationData);
      }
    },
    [onNotification]
  );

  const handleNotificationReadStatus = useCallback(
    (notificationData: Notification): void => {
      if (onNotificationReadStatus) {
        onNotificationReadStatus(notificationData);
      }
    },
    [onNotificationReadStatus]
  );

  const handleError = useCallback(
    (error: Event): void => {
      trackError(new Error('WebSocket notification error'), {
        context: 'websocket_notifications',
        action: 'websocket_error',
      });
    },
    [trackError]
  );

  return useWebSocket({
    enabled,
    url: `notifications/`,
    messageHandlers: {
      notification: handleNotification,
      notification_read_status: handleNotificationReadStatus,
    },
    onError: handleError,
    reconnectAttempts: 5,
    reconnectDelay: 3000,
  });
};
